import { Check, ChevronDown, Search } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import { useClickOutside } from '../../hooks/useClickOutside';

export interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
  /** Chiều cao tối đa của danh sách (px). */
  maxHeight?: number;
}

export function Dropdown({
  value,
  onChange,
  options,
  placeholder = '-- Chọn --',
  disabled = false,
  searchable = true,
  className = '',
  maxHeight = 240,
}: DropdownProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  /** Vị trí tuyệt đối của menu trên màn hình — `null` = chưa đo xong. */
  const [menuStyle, setMenuStyle] = useState<CSSProperties | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((option) => option.value === value);

  useClickOutside(
    [containerRef, menuRef],
    () => {
      setOpen(false);
    },
    open,
  );

  // Mở lại là danh sách sạch, không giữ từ khoá của lần trước.
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  useEffect(() => {
    if (open && searchable) searchRef.current?.focus();
  }, [open, searchable]);

  /**
   * Menu đi qua portal ra `body` và định vị bằng toạ độ màn hình.
   *
   * Bung ngay trong thân component thì mọi khung cha có `overflow-hidden` hay
   * `overflow-x-auto` đều xén nó: thẻ nhóm, khung bảng cuộn ngang, hộp thoại —
   * người dùng thấy ô chọn mở ra nhưng danh sách bị cắt cụt, tưởng là không có
   * dữ liệu. Gần đáy màn hình thì bung LÊN.
   *
   * `useLayoutEffect` để đo trước khi trình duyệt vẽ — bằng `useEffect` sẽ thấy
   * menu nháy ở góc trên bên trái rồi mới nhảy vào chỗ.
   */
  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }

    const update = () => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const spaceBelow = window.innerHeight - rect.bottom;
      const flipUp = spaceBelow < maxHeight + 16 && rect.top > spaceBelow;

      setMenuStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        ...(flipUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
      });
    };

    update();
    window.addEventListener('resize', update);
    // `true` — bắt cả cuộn ở container cha, không chỉ cuộn window. Menu định vị
    // theo viewport nên không tự trôi theo khung cha khi cuộn.
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, maxHeight]);

  const filtered = useMemo(() => {
    if (!searchable || !search) return options;
    const needle = search.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(needle));
  }, [options, search, searchable]);

  // Ba trạng thái LOẠI TRỪ nhau, viết thẳng thay vì ternary lồng — thứ tự ưu
  // tiên (khoá > đang mở > thường) đọc được ngay từ trên xuống.
  let triggerState = 'hover:border-ink-ghost';
  if (disabled) triggerState = 'cursor-not-allowed opacity-50';
  else if (open) triggerState = 'border-accent';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={`border-line bg-canvas flex w-full items-center justify-between gap-2 rounded-lg border px-3.5 py-2.5 pr-9 text-left text-[12px] font-semibold transition-colors ${triggerState} ${
          selected ? 'text-ink' : 'text-ink-dim'
        }`}
      >
        <span className="truncate">{selected ? selected.label : placeholder}</span>
        <ChevronDown
          size={14}
          className={`text-ink-dim shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open &&
        menuStyle &&
        createPortal(
          <div
            ref={menuRef}
            style={menuStyle}
            // `z-[60]`: trên cả lớp phủ hộp thoại (`z-50`), nếu không thì ô chọn
            // trong hộp thoại mở ra là menu nằm dưới lớp phủ, bấm không trúng.
            className="border-line bg-panel z-[60] overflow-hidden rounded-lg border shadow-[0_10px_25px_-5px_rgba(0,0,0,0.5)]"
            role="listbox"
          >
            {searchable && (
              <div className="border-line border-b p-2">
                <div className="relative">
                  <Search
                    size={12}
                    className="text-ink-faint pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                    }}
                    placeholder="Tìm kiếm..."
                    className="border-line bg-canvas text-ink placeholder:text-ink-faint focus:border-accent w-full rounded-md border py-1.5 pl-7 pr-3 text-[12px] outline-none transition-colors"
                  />
                </div>
              </div>
            )}

            <div className="overflow-y-auto py-1" style={{ maxHeight }}>
              {filtered.length === 0 ? (
                <div className="text-ink-dim px-3 py-4 text-center text-[12px]">
                  Không có kết quả
                </div>
              ) : (
                filtered.map((option) => {
                  const isSelected = option.value === value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        onChange(option.value);
                        setOpen(false);
                      }}
                      className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] font-medium transition-colors ${
                        isSelected
                          ? 'bg-accent-soft text-accent-bright'
                          : 'text-ink hover:bg-panel-raised'
                      }`}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <span className="truncate">{option.label}</span>
                      {isSelected && <Check size={12} className="text-accent shrink-0" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
